import React, { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Sprout } from "lucide-react";
import { WfButton } from "@/components/site/primitives";
import { ORG, PHOTO_CREDIT } from "@/content/worldFarmers";
import { scrollToId } from "@/lib/scrollTo";

/* Brand token values used for scroll-driven color interpolation.
   (Framer needs literal color strings; these mirror the CSS design tokens.) */
const WF_WHEAT = "#e9dfc9";
const WF_CLAY = "#c97b63";

export const HeroV4 = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });

  // Shared subtle zoom across the pinned frame
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  // Crossfade three real photos: farm-36 -> farm-3777 -> farm-2611 (base stays)
  const op36 = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const op3777 = useTransform(scrollYProgress, [0.3, 0.6], [1, 0]);
  // Headline words shift color as the story scrolls — both animate identically
  const fieldsColor = useTransform(scrollYProgress, [0, 0.45], [WF_WHEAT, WF_CLAY]);
  const neighborsColor = fieldsColor;

  const go = (id) => (e) => { e.preventDefault(); scrollToId(id); };

  return (
    <section
      id="top"
      ref={ref}
      className="relative w-full bg-[var(--wf-ink)]"
      style={{ height: reduce ? "100svh" : "220vh" }}
    >
      <div className="sticky top-0 h-[100svh] min-h-[640px] w-full overflow-hidden">
        {/* Base layer — final photo, always present so there are no gaps */}
        <motion.img
          src="/images/wf/farm-2611.jpg"
          alt="Golden morning light over the fields as a farmer tends the crops."
          loading="eager"
          style={reduce ? undefined : { scale }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        {/* Mid crossfade photo */}
        {!reduce && (
          <motion.img
            src="/images/wf/farm-fabiola.jpg"
            alt="A farmer in a vibrant patterned dress harvesting leafy greens in a lush green field."
            loading="eager"
            style={{ scale, opacity: op3777 }}
            className="absolute inset-0 h-full w-full object-cover"
            aria-hidden="true"
          />
        )}
        {/* Top / opening photo */}
        <motion.img
          src="/images/wf/farm-36.jpg"
          alt="A smiling woman in a plaid shirt and sun hat standing in a bright field of leafy greens."
          loading="eager"
          style={reduce ? undefined : { scale, opacity: op36 }}
          className="absolute inset-0 h-full w-full object-cover"
          aria-hidden={reduce ? undefined : "true"}
        />

        {/* Top wash for header legibility */}
        <div aria-hidden="true" className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/45 to-transparent" />
        {/* Bottom scrim so the text block stays readable over any photo */}
        <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-black/80 via-black/45 to-transparent" />

        {/* Bottom-anchored, wide, centered content */}
        <div className="absolute inset-x-0 bottom-0">
          <div className="wf-container pb-14 text-center sm:pb-20">
            <div className="mx-auto max-w-4xl">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-white backdrop-blur-sm">
                <Sprout className="h-3.5 w-3.5" aria-hidden="true" />
                Lancaster, MA · Since {ORG.founded}
              </span>
              <h1
                data-testid="hero-headline"
                className="mt-5 font-serif text-4xl font-semibold leading-[1.05] tracking-[-0.01em] text-white drop-shadow-[0_2px_18px_rgba(0,0,0,0.45)] sm:text-6xl lg:text-7xl"
              >
                Fresh from the{" "}
                <motion.span style={reduce ? { color: WF_WHEAT } : { color: fieldsColor }}>fields</motion.span>
                , grown by our{" "}
                <motion.span style={reduce ? { color: WF_WHEAT } : { color: neighborsColor }}>neighbors</motion.span>.
              </h1>
              <p className="mx-auto mt-5 max-w-2xl text-base leading-7 text-white/90 sm:text-lg">
                {ORG.missionShort}
              </p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                <WfButton
                  as="a"
                  href="#get-involved"
                  onClick={go("get-involved")}
                  withArrow
                  className="px-8 py-4 text-base shadow-[0_12px_30px_rgba(107,31,43,0.45)] hover:shadow-[0_16px_40px_rgba(107,31,43,0.55)]"
                  data-testid="hero-primary-cta-button"
                >
                  Get Involved
                </WfButton>
                <WfButton
                  as="a"
                  href="#where-to-buy"
                  onClick={go("where-to-buy")}
                  variant="light"
                  className="px-8 py-4 text-base shadow-[0_12px_30px_rgba(0,0,0,0.35)]"
                  data-testid="hero-secondary-cta-button"
                >
                  Where to buy crops
                </WfButton>
              </div>
            </div>
          </div>
        </div>

        <a
          href={PHOTO_CREDIT.href}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute bottom-3 right-3 z-10 rounded-full bg-black/40 px-3 py-1 text-[11px] font-medium text-white/90 backdrop-blur-sm hover:bg-black/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          {PHOTO_CREDIT.label}
        </a>
      </div>
    </section>
  );
};
